import { isPlatformBrowser, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
  inject,
  PLATFORM_ID,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { debounceTime, distinctUntilChanged, startWith, Subject, takeUntil } from 'rxjs';

import { CardLayout } from '@modules/card/enums/card-layout.enum';
import { LinkTypes } from '@modules/card/enums/link-types.enum';
import { TargetType } from '@modules/card/enums/target-type.enum';
import { CardLinkInterface } from '@modules/card/interfaces/card-link.interface';
import { TARGET_NEW_SPECT } from '@modules/card/interfaces/card.interface';
import { CardModel } from '@modules/card/models/card.model';
import { Screen, WindowService } from '@modules/shared/services/window.service';

import { CardHeroComponent } from '../../card/card-hero/card-hero.component';
import { MediaComponent } from '../../media/media.component';
import { ActionContainerComponent } from '../../ui/action-container/action-container.component';
import { GetResponsiveConfig } from '../configs/carousel-section.config';
import { SliderBaseInterface, SliderBaseItemInterface } from '../interfaces/slider.interface';
import { SliderBaseComponent } from '../slider-base/slider-base.component';

@Component({
  selector: 'mb-carousel-section',
  templateUrl: './carousel-section.component.html',
  styleUrls: ['./carousel-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    SliderBaseComponent,
    RouterLink,
    NgTemplateOutlet,
    CardHeroComponent,
    MediaComponent,
    ActionContainerComponent,
  ],
})
export class CarouselSectionComponent<T extends { id?: string | number }> implements OnInit, OnDestroy {
  private elRef = inject(ElementRef);
  private windowService = inject(WindowService);
  private cdr = inject(ChangeDetectorRef);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);

  private unsubscribe$ = new Subject<boolean>();
  public config!: SliderBaseInterface;
  public cardLayout = CardLayout;
  public linkTypes = LinkTypes;

  public itemsToShow: number = 1;

  @Input() items!: T[];

  private _ratio!: number;
  @Input()
  set ratio(ratio: number) {
    this._ratio = ratio;
    this.elRef.nativeElement.style.setProperty('--carousel-ratio', ratio);
    this.setResponsiveConfig();
  }
  get ratio() {
    return this._ratio;
  }

  public shouldRender = false;

  @Output() openMedia = new EventEmitter();
  @Output() indexChanged = new EventEmitter<number>();

  private setResponsiveConfig() {
    this.config = {
      inGrid: true,
      itemsToShow: 3,
      itemsToSlide: 3,
      breakpoints: GetResponsiveConfig(this.ratio),
    };
  }

  public openCardMedia(data: CardModel) {
    if (!data.link && data.media) {
      this.openMedia.emit(data.media);
    }
  }

  public linkAction(e: MouseEvent, item: CardModel) {
    const { target, query, type, url, outlets } = item.link as CardLinkInterface;
    const programmaticRouting = type === this.linkTypes.PROGRAMMATIC;

    if (target !== TargetType.BLANK && !programmaticRouting && !query) {
      return;
    }
    e.preventDefault();
    if (programmaticRouting || query) {
      this.router
        .navigate([], {
          relativeTo: this.route,
          queryParams: {
            q: query || '',
          },
          queryParamsHandling: 'merge',
        })
        .then(() => {
          this.router.navigate([url], outlets);
        });
      return false;
    }

    window.open.apply(null, [url, '_blank', TARGET_NEW_SPECT]);
    return false;
  }

  public getActiveScreenConfig(screen: Screen): SliderBaseItemInterface {
    let config: SliderBaseItemInterface | null = null;
    if (this.config.breakpoints) {
      if (this.config.breakpoints[screen]) {
        config = this.config.breakpoints[screen] as SliderBaseItemInterface;
      } else {
        const keys = Object.keys(this.config.breakpoints).map((key) => parseInt(key, 10)) as unknown as number[];
        const possibleConfigs = keys.sort((a, b) => a - b).filter((w) => w < (screen as number)) as Screen[];
        if (possibleConfigs?.length) {
          config = this.config.breakpoints[possibleConfigs[possibleConfigs.length - 1]] as SliderBaseItemInterface;
        }
      }
    }
    if (!config) {
      config = this.config;
    }
    return config as SliderBaseItemInterface;
  }

  private isInViewPort(elm: ElementRef): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;

    const rect = elm.nativeElement.getBoundingClientRect();
    // also consider element being visible when it’s almost scrolled into view
    const tolerance = 200;

    return rect.top <= (window.innerHeight || document.documentElement.clientHeight) + tolerance;
  }

  private render() {
    this.shouldRender = true;
    this.unsubscribe$.next(true);
    this.unsubscribe$.complete();
    this.cdr.detectChanges();
  }

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;

    this.windowService.screen$.pipe(takeUntil(this.unsubscribe$)).subscribe((screen) => {
      const config = this.getActiveScreenConfig(screen);
      this.itemsToShow = config.itemsToShow;
    });

    this.windowService.windowScroll$
      .pipe(
        startWith(0), // immediately emit a scroll event // https://github.com/ReactiveX/rxjs/issues/4772
        debounceTime(20),
        takeUntil(this.unsubscribe$),
        distinctUntilChanged()
      )
      .subscribe(() => {
        if (this.isInViewPort(this.elRef)) {
          this.render();
        }
      });
  }

  ngOnDestroy() {
    this.unsubscribe$.next(true);
    this.unsubscribe$.complete();
  }
}
